import paragonBreakpoints from '@edx/paragon/dist/utils/breakpoints';

type ImageFormat = 'avif' | 'webp' | 'jpeg' | 'baseline-jpeg' | 'json';
export type ImageOptions = {
  anim?: boolean;
  background?: string;
  blur?: number;
  border?: {
    color: string;
    width?: number;
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  };
  brightness?: number;
  compression?: 'fast';
  contrast?: number;
  dpr?: number;
  fit?: 'scale-down' | 'contain' | 'cover' | 'crop' | 'pad';
  format?: ImageFormat;
  gamma?: number;
  gravity?:
  'auto' |
  'left' | 'right' | 'top' | 'bottom' |
  { x: number, y: number };
  metadata?: 'keep' | 'copyright' | 'none';
  onerror?: 'redirect';
  quality?: number;
  rotate?: 90 | 180 | 270;
  sharpen?: number;
  trim?: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
    width?: number;
    height?: number;
  };
  width?: number | 'auto';
  height?: number | 'auto';
};
type BreakpointSize = 'extraExtraLarge' | 'extraLarge' | 'large' | 'medium' | 'small' | 'extraSmall';
interface Breakpoint {
  width: number;
  height: number;
  type: BreakpointSize;
}

interface QueriedImage {
  url?: string;
  width?: number;
  height?: number;
}

function addCloudflareImageParams(queriedUrl: string, options: ImageOptions = {}): string {
  if (!queriedUrl) {
    return '';
  }
  if (options.border) {
    const {
      color, top, right, bottom, left, width: borderWidth,
    } = options.border;

    if (!color) {
      throw new Error('Border color must be specified.');
    }

    if (borderWidth && (top || right || bottom || left)) {
      throw new Error('Cannot specify individual border sizes along with general border width.');
    }
  }

  // Contruct the comma separated list of options,
  // see https://developers.cloudflare.com/images/image-resizing/url-format/#options
  let params = '';
  const optionsKeys = Object.keys(options);
  optionsKeys.forEach((opt) => {
    if (typeof options[opt] === 'object') {
      const objKeys = Object.keys(options[opt]);
      objKeys.forEach((key) => {
        const val = options[opt][key];
        params = `${params}${params.length ? ',' : ''}${opt}.${key}=${val}`;
      });
    } else {
      params = `${params}${params.length ? ',' : ''}${opt}=${options[opt]}`;
    }
  });
  const edxCdnDomain = 'https://prod-discovery.edx-cdn.org/';
  const ctfDomain = 'https://images.ctfassets.net/';
  const edxDomain = 'https://www.edx.org/';
  // if the image is in site assets
  if (queriedUrl.startsWith('/')) {
    return `${edxDomain}cdn-cgi/image/${params}${queriedUrl}`;
  }
  // Extract the path from the queriedUrl
  const path = new URL(queriedUrl).pathname;
  // Check the domain type of the URL to determine how to reformat it
  if (queriedUrl.startsWith(edxCdnDomain)) {
    return `${edxCdnDomain}cdn-cgi/image/${params}${path}`;
  }
  if (queriedUrl.startsWith(ctfDomain)) {
    return `${edxDomain}cdn-cgi/image/${params}/contentful${path}`;
  }

  return queriedUrl;
}

const getCloudflareUrlFromQueriedImage = (
  queriedImage?: QueriedImage,
  additionalParams: ImageOptions = {},
): string | null => {
  if (!queriedImage) {
    return null;
  }

  return addCloudflareImageParams(queriedImage.url, {
    width: queriedImage.width,
    height: queriedImage.height,
    format: 'webp',
    ...additionalParams,
  });
};

const generateUrlForBreakpoints = (
  breakpoints: Breakpoint[],
  src: string,
  format: ImageFormat,
): Array<Breakpoint & { url: string }> => {
  if (breakpoints?.length > 0) {
    return breakpoints.reduce((acc, curr) => {
      const { width, height } = curr;
      const url = addCloudflareImageParams(src, { width, height, format });
      return [...acc, { ...curr, url }];
    }, []);
  }

  return [];
};

const generateSrcSet = (breakpointsWithUrl: Array<Breakpoint & { url: string }>): string | null => {
  if (breakpointsWithUrl?.length > 0) {
    return breakpointsWithUrl.map(({ url, type }) => `${url} ${paragonBreakpoints[type].minWidth}w`).join(', ');
  }

  return null;
};

export {
  addCloudflareImageParams,
  getCloudflareUrlFromQueriedImage,
  generateUrlForBreakpoints,
  generateSrcSet,
  Breakpoint,
  BreakpointSize,
  ImageFormat,
};
